import store from '@state/store'
import _404 from '@views/_404.vue'
import { resolveQrCode } from './qr-redirect'
import { quizSlugs } from '@src/data/training-quizzes'
import { adminPageMeta } from '@utils/admin-area'
import {
  ASK_BOWVUE_ENABLED,
  LEGACY_INFRARED_ENABLED,
} from '@utils/feature-flags'

export default [
  {
    path: '/',
    name: 'home',
    // Ask bar parked, so nothing on this page competes with the floating launcher.
    // Restore alongside the bar:
    // meta: {
    //   The hero carries the ask bar for signed-in visitors. Signed out, both it
    //   and the launcher render nothing, so this only matters to the former.
    //   hideChatLauncher: true,
    // },
    component: lazyLoadView(() => import('@views/home')),
  },
  {
    path: '/login',
    name: 'login',
    component: lazyLoadView(() => import('@views/login')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the to dashboard page instead
          next({
            name: 'dashboard',
          })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/newpassword',
    name: 'newpassword',
    component: lazyLoadView(() => import('@views/new-password')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the dashboard page instead
          next({
            name: 'dashboard',
          })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/resetpassword',
    name: 'resetpassword',
    component: lazyLoadView(() => import('@views/reset-password')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the dashboard page instead
          next({
            name: 'dashboard',
          })
        } else {
          // Continue to the reset password page
          next()
        }
      },
    },
  },
  {
    path: '/confirmresetpassword',
    name: 'confirmresetpassword',
    component: lazyLoadView(() => import('@views/confirm-password-reset')),
    props: (route) => ({
      email: route.query.email,
    }),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the dashboard page instead
          next({
            name: 'dashboard',
          })
        } else {
          // Continue to the reset password page
          next()
        }
      },
    },
  },
  {
    path: '/profile',
    name: 'profile',
    component: lazyLoadView(() => import('@views/profile')),
    meta: {
      authRequired: true,
    },
    props: (route) => ({
      user: store.state.auth.currentUser,
    }),
  },
  // Ask BowVue (AI assistant). Flag-gated together with the floating
  // ChatWidget in layouts/main.vue (see utils/feature-flags). With the flag
  // off, the route doesn't exist and /ask-bowvue falls to the 404 catch-all.
  ...(ASK_BOWVUE_ENABLED
    ? [
        {
          path: '/ask-bowvue',
          name: 'ask-bowvue',
          component: lazyLoadView(() => import('@views/ask-bowvue')),
          meta: {
            authRequired: true,
            // The floating widget hides here. The page IS the conversation.
            hideChatWidget: true,
          },
        },
      ]
    : []),
  {
    path: '/contact-us',
    name: 'contact us',
    component: lazyLoadView(() => import('@views/contact-us')),
  },
  // {
  //   path: '/profile/:email',
  //   name: 'email-profile',
  //   component: lazyLoadView(() => import('@views/profile')),
  //   meta: {
  //     authRequired: true,
  //   },
  //   beforeResolve(routeTo, routeFrom, next) {
  //     store
  //       // Try to fetch the user's information by their email
  //       .dispatch('users/fetchUser', { email: routeTo.params.email })
  //       .then(user => {
  //         // Add the user to the route params, so that it can
  //         // be provided as a prop for the view component below.
  //         routeTo.params.user = user
  //         // Continue to the route.
  //         next()
  //       })
  //       .catch(() => {
  //         // If a user with the provided email could not be
  //         // found, redirect to the 404 page.
  //         next({ name: '404', params: { resource: 'User' } })
  //       })
  //   },
  //   // Set the user from the route params, once it's set in the
  //   // beforeEnter route guard.
  //   props: route => ({ user: route.params.user }),
  // },
  {
    path: '/qr',
    redirect: {
      name: 'dashboard',
    },
  },
  {
    path: '/qr/:id',
    name: 'qrcode',
    meta: {
      beforeResolve: resolveQrCode,
    },
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        store
          .dispatch('auth/logOut')
          // A failed REMOTE sign-out (offline, Cognito down) is swallowed on
          // purpose: `auth/logOut` clears the local session in its own
          // `finally`, the user is leaving, and there's no UI left to surface an
          // error on. Without this the rejection propagated through `.finally`
          // with nothing to catch it. An unhandled promise rejection on every
          // failed sign-out.
          .catch(() => {})
          .finally(() => {
            const authRequiredOnPreviousRoute = routeFrom.matched.some(
              (route) => route.meta.authRequired
            )
            // Navigate back to previous page, or home as a fallback
            next(
              authRequiredOnPreviousRoute
                ? {
                    name: 'home',
                  }
                : {
                    ...routeFrom,
                  }
            )
          })
      },
    },
  },
  {
    path: '/locations',
    name: 'locations',
    meta: {
      authRequired: true,
      // Ask bar parked, so the launcher is back on this page unconditionally.
      // Restore alongside the bar:
      // Carries the ask bar in its header, so the launcher stands down here until
      // there is a conversation to reopen. See the dashboard route above and
      // ChatWidget.showLauncher.
      // hideChatLauncher: true,
    },
    component: lazyLoadView(() => import('@views/locations')),
    props: (route) => ({
      signedin: store.getters['auth/loggedIn'],
    }),
  },
  {
    // Client-facing usage guide (see how-to.vue). Covers only pages clients
    // can reach, no admin areas.
    path: '/how-to',
    name: 'how-to',
    meta: {
      authRequired: true,
    },
    component: lazyLoadView(() => import('@views/how-to')),
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    meta: {
      authRequired: true,
      // Ask bar parked, so the launcher is back on this page unconditionally.
      // Restore alongside the bar:
      // The dashboard header carries the ask bar, which opens the same chat dialog
      // the floating launcher does. Two controls for one thing on one screen reads
      // as two features, so the launcher stands down here. But only until there is
      // a conversation to reopen (see ChatWidget.showLauncher). It is unconditional
      // on every other page.
      // hideChatLauncher: true,
    },
    component: lazyLoadView(() => import('@views/bv-dashboard')),
    props: (route) => ({
      signedin: store.getters['auth/loggedIn'],
    }),
  },
  {
    path: '/location/:locationId',
    name: 'location-detail',
    meta: {
      authRequired: true,
      // Ask bar parked, so the launcher is back on this page unconditionally.
      // Restore alongside the bar:
      // Carries the ask bar in its header, so the launcher stands down here until
      // there is a conversation to reopen. See the dashboard route above and
      // ChatWidget.showLauncher.
      // hideChatLauncher: true,
    },
    component: lazyLoadView(() => import('@views/location')),
    props: (route) => ({
      signedin: store.getters['auth/loggedIn'],
      locationId: route.params.locationId,
    }),
  },
  // {
  //   path: '/gantt',
  //   name: 'gantt',
  //   meta: {
  //     authRequired: true,
  //   },
  //   component: lazyLoadView(() => import('@views/gantt')),
  // },
  {
    path: '/devices',
    name: 'devices',
    meta: {
      authRequired: true,
    },
    component: lazyLoadView(() => import('@views/devices')),
    // Set the location for device list from the route params
    props: (route) => ({
      // locationFilter: route.params.locationFilter,
      locationId: route.query.locationId,
      locationName: route.query.locationName,
    }),
  },
  {
    path: '/device/:deviceId',
    name: 'device-detail',
    meta: {
      // Public on purpose: a scanned QR code shows basic device info without
      // logging in. The Documents / LOTO / Qualified Workers tabs (and the
      // authenticated device query) are gated inside the view by `loggedIn`.
    },
    component: lazyLoadView(() => import('@views/device')),
    props: (route) => ({
      signedin: store.getters['auth/loggedIn'],
      deviceId: route.params.deviceId,
      // The scanned label's code, set by the /qr/:id redirect. A visitor who
      // is not logged in sees the public view only with it.
      qrCode: route.query.qr || '',
    }),
  },
  {
    path: '/infrared',
    name: 'infrared',
    component: lazyLoadView(() => import('@views/infrared')),
    // Whoever reads infrared; what each can change is decided on the page
    // (infrared.write) and again by the API, at the device's site.
    meta: {
      authRequired: true,
      permissionRequired: 'infrared.read',
    },
  },
  // Legacy Infrared archive. Read-only records from the retired system.
  //
  // Flag-gated (see utils/feature-flags): with the flag off the route does not
  // exist and /legacy-infrared falls through to the 404 catch-all, the same way
  // Ask BowVue is handled above. Turned off 2026-08-11, when the retired system
  // went back into service and the imported copy stopped being current.
  //
  // infrared.legacy (admins only, as seeded), matching the server on every
  // legacyIR* query: most of the archive's 48 customers are companies Bowvue no
  // longer serves. Drill-down state rides in the query string, so this one
  // record covers every level of the archive.
  ...(LEGACY_INFRARED_ENABLED
    ? [
        {
          path: '/legacy-infrared',
          name: 'legacy-infrared',
          component: lazyLoadView(() => import('@views/legacy-infrared')),
          meta: {
            authRequired: true,
            permissionRequired: 'infrared.legacy',
          },
        },
      ]
    : []),
  {
    // LOTO: whoever reads it (module permission loto.read: agents, and customers
    // at their own sites); what each can change is decided on the page and again
    // by the API, at the equipment's site.
    // Optional params so the page's selection lives in the URL: /loto,
    // /loto/<locationId>, /loto/<locationId>/<deviceId>. That is what makes a
    // selection linkable, bookmarkable, survivable across a refresh, and
    // encodable in a QR code stuck to a machine.
    path: '/loto/:locationId?/:deviceId?',
    name: 'loto',
    component: lazyLoadView(() => import('@views/loto')),
    props: (route) => ({
      routeLocationId: route.params.locationId || null,
      routeDeviceId: route.params.deviceId || null,
    }),
    // Was adminRequired, which locked agents out "during the initial production
    // rollout", then ADMIN or AGENT. A technician scanning a QR code on the
    // floor is an agent, and since 2026-10-15 a customer reads LOTO too, so the
    // gate is the permission rather than a list of roles.
    meta: {
      authRequired: true,
      permissionRequired: 'loto.read',
    },
  },
  {
    // The admin area. Each page is gated by its module permissions
    // (src/utils/admin-area.js); the area itself by holding any of them, so the
    // landing page always has something on it.
    path: '/admin',
    component: lazyLoadView(() => import('@views/admin-base')),
    meta: {
      authRequired: true,
      adminAreaRequired: true,
    },
    children: [
      {
        // Admin landing hub (card grid of the admin destinations the viewer may
        // open). Reached via the Admin button in the nav bar.
        path: '',
        name: 'admin',
        component: lazyLoadView(() => import('@views/admin/admin-home')),
        meta: {
          authRequired: true,
          adminAreaRequired: true,
        },
      },
      {
        path: 'users',
        name: 'admin-users',
        component: lazyLoadView(() => import('@views/admin/admin-users')),
        meta: adminPageMeta('admin-users'),
      },
      {
        path: 'users/:id',
        name: 'admin-user-detail',
        component: lazyLoadView(() => import('@views/admin/admin-user-detail')),
        meta: adminPageMeta('admin-user-detail'),
        props: (route) => ({
          userId: route.params.id,
        }),
      },
      {
        path: 'locations',
        name: 'location-list',
        component: lazyLoadView(() => import('@views/admin/locations')),
        meta: adminPageMeta('location-list'),
      },
      {
        path: 'location-users',
        name: 'location-users',
        component: () =>
          lazyLoadView(import('@views/admin/admin-location-users')),
        meta: adminPageMeta('location-users'),
        props: (route) => ({
          locationId: route.query.id,
        }),
      },
      {
        // Editing a device and taking it in or out of service, for any location
        // rather than only the caller's: the location list plus a device write.
        path: 'devices',
        name: 'admin-devices',
        component: lazyLoadView(() => import('@views/admin/devices')),
        meta: adminPageMeta('admin-devices'),
      },
      {
        // Compliance view across every site, not scoped to the caller's
        // locations: locations.renewals.
        path: 'study-renewals',
        name: 'study-renewals',
        component: lazyLoadView(() => import('@views/admin/study-renewals')),
        meta: adminPageMeta('study-renewals'),
      },
      {
        path: 'importarcflash',
        name: 'import-arc-flash',
        component: lazyLoadView(() => import('@views/admin/arc-flash-import')),
        meta: adminPageMeta('import-arc-flash'),
      },
      {
        path: 'importdocument',
        name: 'import-document',
        component: lazyLoadView(() => import('@views/admin/document-import')),
        meta: adminPageMeta('import-document'),
      },
      {
        path: 'importtrainingdocuments',
        name: 'import-training-documents',
        component: lazyLoadView(() => import('@views/admin/training-import')),
        meta: adminPageMeta('import-training-documents'),
      },
      {
        path: 'importlotodocuments',
        name: 'import-loto',
        component: lazyLoadView(() => import('@views/admin/loto-import')),
        meta: adminPageMeta('import-loto'),
      },
      {
        path: 'importprojectdocuments',
        name: 'import-project-documents',
        component: () =>
          lazyLoadView(import('@views/admin/project-document-import')),
        meta: adminPageMeta('import-project-documents'),
      },
      // Compress Files: shrinks PDFs and photos and zips files, in the browser.
      {
        path: 'compress',
        name: 'compress-files',
        component: lazyLoadView(() => import('@views/admin/compress-files')),
        meta: adminPageMeta('compress-files'),
      },
      {
        path: 'support-tickets',
        name: 'support-tickets',
        component: () =>
          lazyLoadView(import('@views/admin/admin-support-tickets')),
        meta: adminPageMeta('support-tickets'),
      },
      // What each role may do, module by module (RBAC). Admins and developers,
      // like the API behind it.
      {
        path: 'permissions',
        name: 'role-permissions',
        component: lazyLoadView(() => import('@views/admin/role-permissions')),
        meta: adminPageMeta('role-permissions'),
      },
      // The API server's recent log entries. DEVELOPER only: unlike every other
      // page here, admins may NOT open it (the API refuses them too). Not in
      // ADMIN_PAGES for that reason; that table is admins' and developers' pages.
      {
        path: 'server-logs',
        name: 'server-logs',
        component: lazyLoadView(() => import('@views/admin/server-logs')),
        meta: { authRequired: true, rolesRequired: ['DEVELOPER'] },
      },
      // The old User Roles page, folded into the user's own page (2026-10-23):
      // its address lands on that page's Roles card, so bookmarks and links
      // still work.
      {
        path: 'roles/:id',
        name: 'roles',
        redirect: (to) => ({
          name: 'admin-user-detail',
          params: { id: to.params.id },
          hash: '#roles',
        }),
        meta: adminPageMeta('roles'),
      },
    ],
  },
  {
    path: '/terms',
    name: 'terms',
    component: lazyLoadView(() => import('@views/terms')),
  },
  {
    path: '/training-results',
    name: 'training-results',
    component: lazyLoadView(() => import('@views/training-results')),
    // Internal: training.read (admins and developers, as seeded).
    meta: adminPageMeta('training-results'),
  },
  {
    // Internal staff scheduling board: scheduling.read (admins, as seeded).
    path: '/schedule',
    name: 'schedule',
    component: lazyLoadView(() => import('@views/schedule')),
    meta: adminPageMeta('schedule'),
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: lazyLoadView(() => import('@views/notifications')),
    // Self-scoped to the caller; any authenticated user just sees their own
    // (empty if none), so no role gate.
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/training/:quizSlug',
    name: 'training-quiz',
    // Public on purpose: reached by link / QR without logging in (same pattern
    // as /device/:deviceId). Four quizzes share this one param route. One
    // distinct URL per quiz, ready for four QR codes. Unknown slugs 404 in the
    // guard below so we never render an empty quiz shell.
    component: lazyLoadView(() => import('@views/training-quiz')),
    props: (route) => ({
      quizSlug: route.params.quizSlug,
    }),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        if (quizSlugs.includes(routeTo.params.quizSlug)) {
          next()
        } else {
          next({ name: '404', params: { resource: 'Quiz' } })
        }
      },
    },
  },
  {
    path: '/404',
    name: '404',
    component: _404,
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    // Vue Router 4 requires a named catch-all param instead of '*'.
    path: '/:pathMatch(.*)*',
    redirect: '/404',
  },
]

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: lazyLoadView(() => import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
// Vue Router 4 lazy-loads a route component from a function returning a
// dynamic import(). This thin wrapper keeps the existing call sites readable.
// (The old Vue-2 loading/timeout async-component wrapper is no longer needed,
// Router 4 resolves async components natively.)
function lazyLoadView(loader) {
  return loader
}
